import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { adminAPI, adminApi } from '../../lib/api';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Loader2, Search, Building2, Users, Truck, Package, CreditCard, Shield,
  MoreVertical, Power, PowerOff, Trash2, KeyRound, RefreshCw, Eye,
  CheckCircle, XCircle, AlertTriangle, ChevronLeft, ChevronRight, RotateCcw,
  Sparkles, Flame, AlertOctagon, Calendar, X, UserCog, Monitor,
  DollarSign, Zap
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../../components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Switch } from '../../components/ui/switch';
import { formatDistanceToNow, format } from 'date-fns';
import { toast } from 'sonner';
import { AdminScreenShareViewer } from '../../components/AdminScreenShareViewer';
import { getCountryName, getIndustryLabel } from '../../lib/i18n-config';

export const AdminTenants = () => {
  const navigate = useNavigate();
  const { adminRole } = useOutletContext();
  const isPrimaryAdmin = adminRole === 'primary';
  const { startImpersonation, isImpersonating } = useImpersonation();
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [tourFilter, setTourFilter] = useState('all');
  const [healthFilter, setHealthFilter] = useState('all');
  const [hideTestTenants, setHideTestTenants] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Selected tenant for detail view
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Action dialogs
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, tenant: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  
  // Impersonation modal state
  const [impersonateModal, setImpersonateModal] = useState({ open: false, tenant: null });
  const [impersonateReadOnly, setImpersonateReadOnly] = useState(false);
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const [impersonateError, setImpersonateError] = useState(null);
  
  // Beta toggle state
  const [betaToggleLoading, setBetaToggleLoading] = useState(false);
  const [addonBypassLoading, setAddonBypassLoading] = useState(false);
  
  // CRM Beta toggle state
  const [crmBetaLoading, setCrmBetaLoading] = useState(false);
  const [crmBetaDays, setCrmBetaDays] = useState(30);
  
  // Billing override modal
  const [billingOverrideModal, setBillingOverrideModal] = useState({ open: false, tenant: null });
  const [billingOverrideForm, setBillingOverrideForm] = useState({ mode: 'comped', plan: 'starter', reason: '', expires_at: '' });
  const [billingOverrideLoading, setBillingOverrideLoading] = useState(false);
  
  // Create comped tenant modal
  const [compedCreateModal, setCompedCreateModal] = useState(false);
  const [compedCreateForm, setCompedCreateForm] = useState({ tenant_name: '', owner_name: '', owner_email: '', product_family: 'inventory_only', comped_plan: 'starter', reason: '' });
  const [compedCreateLoading, setCompedCreateLoading] = useState(false);
  
  // Server-side tenant stats (including MRR)
  const [tenantStats, setTenantStats] = useState({
    total_tenants: 0,
    active_count: 0,
    inactive_count: 0,
    deleted_count: 0,
    total_mrr_cents: 0
  });
  
  // Purge Test Tenants modal state
  const [purgeModalOpen, setPurgeModalOpen] = useState(false);
  const [purgeStep, setPurgeStep] = useState('initial'); // initial, dry-run, confirm, executing, done
  const [purgeDryRunData, setPurgeDryRunData] = useState(null);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeConfirmPhrase, setPurgeConfirmPhrase] = useState('');
  const [purgeResult, setPurgeResult] = useState(null);
  const PURGE_CONFIRM_PHRASE = 'DELETE TEST TENANTS';
  const FORCE_CONFIRM_PHRASE = 'I UNDERSTAND THIS IS PERMANENT';
  
  // Force risky delete state
  const [forceRisky, setForceRisky] = useState(false);
  const [forceConfirmPhrase, setForceConfirmPhrase] = useState('');
  
  // Test domain allowlist state
  const [testDomains, setTestDomains] = useState([]);
  const [newDomainInput, setNewDomainInput] = useState('');
  const [domainLoading, setDomainLoading] = useState(false);
  
  // Screen Share state
  const [screenShareModal, setScreenShareModal] = useState({ open: false, tenant: null });
  const [screenShareSession, setScreenShareSession] = useState(null);
  const [screenShareLoading, setScreenShareLoading] = useState(false);
  const [screenShareViewerOpen, setScreenShareViewerOpen] = useState(false);

  // Dev helper: force subscription badge override
  const [devBadgeOverride, setDevBadgeOverride] = useState(null); // tenant id or null

  // Drip timeline drawer state
  const [dripDrawer, setDripDrawer] = useState({ open: false, tenantId: null, tenantName: null });
  const [dripTimeline, setDripTimeline] = useState(null);
  const [dripTimelineLoading, setDripTimelineLoading] = useState(false);
  const [dripPreview, setDripPreview] = useState(null);
  const [dripPreviewLoading, setDripPreviewLoading] = useState(false);
  
  // DEV-ONLY: Single Tenant Purge modal state
  const [tenantPurgeModal, setTenantPurgeModal] = useState({ 
    open: false, 
    tenant: null,
    preview: null,
    step: 'preview' // preview, confirm, result
  });
  const [tenantPurgeForm, setTenantPurgeForm] = useState({
    nameConfirm: '',
    irreversibleConfirm: false
  });
  const [tenantPurgeLoading, setTenantPurgeLoading] = useState(false);
  const [tenantPurgeResult, setTenantPurgeResult] = useState(null);

  // Per-user password reset state
  const [userResetState, setUserResetState] = useState({ user: null, step: null, tempPassword: null, loading: false });

  const handleUserPasswordReset = async (user) => {
    setUserResetState({ user, step: 'confirm', tempPassword: null, loading: false });
  };

  const confirmUserPasswordReset = async () => {
    const { user } = userResetState;
    if (!user) return;
    setUserResetState(prev => ({ ...prev, loading: true }));
    try {
      const res = await adminAPI.resetUserPassword(user.id);
      setUserResetState({
        user,
        step: 'result',
        tempPassword: res.data.temporary_password,
        loading: false
      });
      // Update the badge in the detail view
      if (tenantDetail?.users) {
        const updated = tenantDetail.users.map(u =>
          u.id === user.id ? { ...u, force_password_change: true } : u
        );
        setTenantDetail(prev => ({ ...prev, users: updated }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
      setUserResetState({ user: null, step: null, tempPassword: null, loading: false });
    }
  };

  const closeUserResetDialog = () => {
    setUserResetState({ user: null, step: null, tempPassword: null, loading: false });
  };

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20, hide_test: hideTestTenants };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (tourFilter && tourFilter !== 'all') params.tour_status = tourFilter;
      if (healthFilter && healthFilter !== 'all') params.health = healthFilter;
      if (searchQuery) params.search = searchQuery;
      
      // Fire tenants first (critical), stats/stripe in parallel (non-critical)
      const tenantsRes = await adminAPI.getTenants(params);
      
      setTenants(tenantsRes.data.tenants);
      setTotal(tenantsRes.data.total);
      setPages(tenantsRes.data.pages);
      
      // Load non-critical data in background (don't block tenant list)
      Promise.all([
        adminAPI.getTenantStats().catch(() => ({ data: null })),
      ]).then(([statsRes]) => {
        if (statsRes.data) {
          setTenantStats(statsRes.data);
        }
      });
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        console.error('Failed to fetch tenants:', err);
        toast.error('Failed to load tenants. The server may be slow — try refreshing.');
      }
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, tourFilter, healthFilter, hideTestTenants, searchQuery, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchTenants();
  }, [fetchTenants, navigate]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setPage(1);
  };

  const fetchTenantDetail = async (tenantId) => {
    setDetailLoading(true);
    try {
      const response = await adminAPI.getTenant(tenantId);
      setTenantDetail(response.data);
    } catch (err) {
      console.error('Failed to fetch tenant detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const openTenantDetail = (tenant) => {
    setSelectedTenant(tenant);
    fetchTenantDetail(tenant.id);
  };

  const closeTenantDetail = () => {
    setSelectedTenant(null);
    setTenantDetail(null);
  };

  const openActionDialog = (type, tenant) => {
    setActionDialog({ open: true, type, tenant });
    setActionResult(null);
  };

  const closeActionDialog = () => {
    setActionDialog({ open: false, type: null, tenant: null });
    setActionResult(null);
  };

  const executeAction = async () => {
    const { type, tenant } = actionDialog;
    setActionLoading(true);
    setActionResult(null);
    
    try {
      let response;
      switch (type) {
        case 'deactivate':
          response = await adminAPI.deactivateTenant(tenant.id);
          break;
        case 'activate':
          response = await adminAPI.activateTenant(tenant.id);
          break;
        case 'delete':
          response = await adminAPI.softDeleteTenant(tenant.id);
          break;
        case 'restore':
          response = await adminAPI.restoreTenant(tenant.id);
          break;
        case 'reset-password':
          response = await adminAPI.resetOwnerPassword(tenant.id);
          setActionResult({ success: true, data: response.data });
          setActionLoading(false);
          fetchTenants();
          return; // Don't close dialog for password reset
        default:
          throw new Error('Unknown action');
      }
      
      setActionResult({ success: true, message: response.data.message });
      fetchTenants();
      
      // Refresh detail if open
      if (selectedTenant?.id === tenant.id) {
        fetchTenantDetail(tenant.id);
      }
      
      setTimeout(() => closeActionDialog(), 1500);
    } catch (err) {
      setActionResult({ 
        success: false, 
        message: err.response?.data?.detail || 'Action failed' 
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleBetaToggle = async (tenantId, newBetaStatus) => {
    setBetaToggleLoading(true);
    try {
      await adminAPI.setTenantBetaStatus(tenantId, newBetaStatus);
      
      // Refresh tenant detail to get updated status
      if (selectedTenant?.id === tenantId) {
        await fetchTenantDetail(tenantId);
      }
      
      // Refresh tenants list
      fetchTenants();
    } catch (err) {
      console.error('Failed to toggle beta status:', err);
      alert(err.response?.data?.detail || 'Failed to update beta status');
    } finally {
      setBetaToggleLoading(false);
    }
  };

  const handleAddonBypassToggle = async (tenantId, allow) => {
    setAddonBypassLoading(true);
    try {
      await adminAPI.toggleAddonBypass(tenantId, allow);
      if (selectedTenant?.id === tenantId) {
        await fetchTenantDetail(tenantId);
      }
      fetchTenants();
    } catch (err) {
      console.error('Failed to toggle addon bypass:', err);
      alert(err.response?.data?.detail || 'Failed to update addon bypass');
    } finally {
      setAddonBypassLoading(false);
    }
  };

  // CRM Beta toggle handler
  const handleCrmBetaToggle = async (tenantId, enable) => {
    setCrmBetaLoading(true);
    try {
      await adminAPI.toggleCrmBeta(tenantId, enable, enable ? crmBetaDays : null);
      if (selectedTenant?.id === tenantId) {
        await fetchTenantDetail(tenantId);
      }
      fetchTenants();
    } catch (err) {
      console.error('Failed to toggle CRM Beta:', err);
      alert(err.response?.data?.detail || 'Failed to update CRM Beta');
    } finally {
      setCrmBetaLoading(false);
    }
  };

  // Impersonation handlers
  const openImpersonateModal = (tenant) => {
    setImpersonateModal({ open: true, tenant });
    setImpersonateReadOnly(false);
    setImpersonateError(null);
  };

  const closeImpersonateModal = () => {
    setImpersonateModal({ open: false, tenant: null });
    setImpersonateReadOnly(false);
    setImpersonateError(null);
  };

  const handleStartImpersonation = async () => {
    const { tenant } = impersonateModal;
    if (!tenant) return;

    setImpersonateLoading(true);
    setImpersonateError(null);

    try {
      const result = await startImpersonation(tenant.id, impersonateReadOnly);
      
      if (result.success) {
        closeImpersonateModal();
        // Navigate to the tenant's dashboard
        navigate('/dashboard');
      } else {
        setImpersonateError(result.error);
      }
    } catch (err) {
      setImpersonateError(err.response?.data?.detail || 'Failed to start impersonation');
    } finally {
      setImpersonateLoading(false);
    }
  };

  // Screen Share handlers
  const openScreenShareModal = (tenant) => {
    setScreenShareModal({ open: true, tenant });
  };
  
  const closeScreenShareModal = () => {
    setScreenShareModal({ open: false, tenant: null });
  };
  
  const handleStartScreenShare = async () => {
    if (!screenShareModal.tenant) return;
    
    setScreenShareLoading(true);
    try {
      const response = await adminAPI.createSupportSession(screenShareModal.tenant.id);
      const sessionData = {
        ...response.data,
        tenant_name: screenShareModal.tenant.name
      };
      setScreenShareSession(sessionData);
      closeScreenShareModal();
      setScreenShareViewerOpen(true);
      toast.success('Support session created');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to create support session';
      toast.error(errorMsg);
    } finally {
      setScreenShareLoading(false);
    }
  };
  
  const handleEndScreenShare = async () => {
    if (screenShareSession?.session_id) {
      try {
        await adminAPI.endSupportSession(screenShareSession.session_id);
      } catch (err) {
        console.error('Failed to end session:', err);
      }
    }
    setScreenShareSession(null);
    setScreenShareViewerOpen(false);
  };

  // Drip timeline handlers
  const openDripDrawer = async (tenantId, tenantName) => {
    setDripDrawer({ open: true, tenantId, tenantName });
    setDripTimeline(null);
    setDripPreview(null);
    setDripTimelineLoading(true);
    try {
      const res = await adminApi.get(`/admin/tenants/${tenantId}/trial-email-timeline`);
      setDripTimeline(res.data);
    } catch (err) {
      toast.error('Failed to load drip timeline');
    } finally {
      setDripTimelineLoading(false);
    }
  };

  const loadEmailPreview = async (emailLogId) => {
    setDripPreviewLoading(true);
    setDripPreview(null);
    try {
      const res = await adminApi.get(`/admin/email-logs/${emailLogId}/preview`);
      setDripPreview(res.data);
    } catch (err) {
      toast.error('Failed to load email preview');
    } finally {
      setDripPreviewLoading(false);
    }
  };

  const loadNextEmailPreview = async (tenantId) => {
    setDripPreviewLoading(true);
    setDripPreview(null);
    try {
      const res = await adminApi.get(`/admin/tenants/${tenantId}/trial-email-preview-next`);
      if (res.data.preview) {
        setDripPreview(res.data.preview);
      } else {
        toast.info(res.data.message || 'No next email to preview');
      }
    } catch (err) {
      toast.error('Failed to load next email preview');
    } finally {
      setDripPreviewLoading(false);
    }
  };

  // DEV-ONLY: Single Tenant Purge handlers
  const openTenantPurgeModal = async (tenant) => {
    setTenantPurgeForm({ nameConfirm: '', irreversibleConfirm: false });
    setTenantPurgeResult(null);
    setTenantPurgeLoading(true);
    setTenantPurgeModal({ 
      open: true, 
      tenant, 
      preview: null,
      step: 'preview' 
    });
    
    try {
      const response = await adminAPI.previewTenantPurge(tenant.id);
      setTenantPurgeModal(prev => ({ 
        ...prev, 
        preview: response.data,
        step: response.data.purge_enabled ? 'confirm' : 'disabled'
      }));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to load purge preview';
      toast.error(errorMsg);
      setTenantPurgeModal(prev => ({ ...prev, step: 'error', preview: { error: errorMsg } }));
    } finally {
      setTenantPurgeLoading(false);
    }
  };

  const closeTenantPurgeModal = () => {
    const hadSuccess = tenantPurgeResult?.success;
    setTenantPurgeModal({ open: false, tenant: null, preview: null, step: 'preview' });
    setTenantPurgeForm({ nameConfirm: '', irreversibleConfirm: false });
    setTenantPurgeResult(null);
    // Safety: ensure body/html pointer-events/overflow are restored (Radix body-lock cleanup)
    // Use multiple frames to ensure Radix has fully cleaned up
    const cleanupPointerEvents = () => {
      document.body.style.pointerEvents = '';
      document.documentElement.style.pointerEvents = '';
      // Also remove any Radix-specific data attributes that might cause issues
      document.body.style.overflow = '';
    };
    // Run cleanup immediately and after a frame to catch any race conditions
    cleanupPointerEvents();
    requestAnimationFrame(() => {
      cleanupPointerEvents();
      // Double-check after another frame
      requestAnimationFrame(cleanupPointerEvents);
    });
    // Refresh tenant list AFTER dialog closes (avoids Radix body-lock desync)
    if (hadSuccess) {
      // Delay fetchTenants slightly to ensure dialog is fully unmounted
      setTimeout(() => {
        fetchTenants();
      }, 100);
    }
  };

  const executeTenantPurge = async () => {
    const { tenant, preview } = tenantPurgeModal;
    if (!tenant) return;
    
    // Validate form
    if (tenantPurgeForm.nameConfirm !== preview?.tenant_name) {
      toast.error('Tenant name does not match');
      return;
    }
    if (!tenantPurgeForm.irreversibleConfirm) {
      toast.error('You must confirm this is irreversible');
      return;
    }
    
    setTenantPurgeLoading(true);
    try {
      const response = await adminAPI.purgeTenant(
        tenant.id,
        tenantPurgeForm.nameConfirm,
        tenantPurgeForm.irreversibleConfirm
      );
      setTenantPurgeResult(response.data);
      setTenantPurgeModal(prev => ({ ...prev, step: 'result' }));
      toast.success(`Tenant "${preview.tenant_name}" has been permanently deleted`);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to purge tenant';
      toast.error(errorMsg);
      setTenantPurgeResult({ success: false, error: errorMsg });
    } finally {
      setTenantPurgeLoading(false);
    }
  };

  // Purge Test Tenants handlers
  const openPurgeModal = async () => {
    setPurgeModalOpen(true);
    setPurgeStep('initial');
    setPurgeDryRunData(null);
    setPurgeConfirmPhrase('');
    setPurgeResult(null);
    // Load domains when opening modal
    await fetchTestDomains();
  };

  const closePurgeModal = () => {
    setPurgeModalOpen(false);
    setPurgeStep('initial');
    setPurgeDryRunData(null);
    setPurgeConfirmPhrase('');
    setPurgeResult(null);
    setForceRisky(false);
    setForceConfirmPhrase('');
    // Refresh tenant list if we made changes
    if (purgeResult?.deleted_count > 0) {
      fetchTenants();
    }
  };
  
  const fetchTestDomains = async () => {
    try {
      const response = await adminAPI.getTestDomains();
      setTestDomains(response.data.domains || []);
    } catch (err) {
      console.error('Failed to load test domains:', err);
    }
  };
  
  const handleAddDomain = async () => {
    if (!newDomainInput.trim()) return;
    setDomainLoading(true);
    try {
      await adminAPI.addTestDomain(newDomainInput.trim());
      setNewDomainInput('');
      await fetchTestDomains();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add domain');
    } finally {
      setDomainLoading(false);
    }
  };
  
  const handleRemoveDomain = async (domainId) => {
    setDomainLoading(true);
    try {
      await adminAPI.removeTestDomain(domainId);
      await fetchTestDomains();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove domain');
    } finally {
      setDomainLoading(false);
    }
  };

  const [purgeJobId, setPurgeJobId] = useState(null);
  const [purgeJobStatus, setPurgeJobStatus] = useState(null);
  const purgePollRef = React.useRef(null);

  const handlePurgeDryRun = async () => {
    setPurgeLoading(true);
    try {
      const response = await adminAPI.bulkHardDeleteDryRun(forceRisky);
      setPurgeDryRunData(response.data);
      setPurgeStep('dry-run');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to run dry run');
    } finally {
      setPurgeLoading(false);
    }
  };

  // Poll a purge job until it leaves the running/queued state.
  const startPollingPurgeJob = (jobId) => {
    if (purgePollRef.current) clearInterval(purgePollRef.current);
    const poll = async () => {
      try {
        const resp = await adminAPI.getPurgeJob(jobId);
        setPurgeJobStatus(resp.data);
        const s = resp.data?.status;
        if (s && s !== 'queued' && s !== 'running') {
          if (purgePollRef.current) {
            clearInterval(purgePollRef.current);
            purgePollRef.current = null;
          }
          setPurgeResult({
            success: s === 'completed',
            ...resp.data,
          });
          setPurgeStep('done');
          // Refresh tenant list so the table reflects the new state.
          fetchTenants().catch(() => {});
        }
      } catch (err) {
        // Network blip — keep polling; only surface after the next failure
        console.warn('purge poll error', err?.message);
      }
    };
    poll();
    purgePollRef.current = setInterval(poll, 2000);
  };

  // Cleanup interval on unmount.
  useEffect(() => {
    return () => {
      if (purgePollRef.current) clearInterval(purgePollRef.current);
    };
  }, []);

  const handlePurgeExecute = async () => {
    if (purgeConfirmPhrase !== PURGE_CONFIRM_PHRASE) {
      alert(`Please type "${PURGE_CONFIRM_PHRASE}" to confirm`);
      return;
    }
    if (forceRisky && forceConfirmPhrase !== FORCE_CONFIRM_PHRASE) {
      alert(`To force delete risky tenants, please type "${FORCE_CONFIRM_PHRASE}"`);
      return;
    }

    setPurgeStep('executing');
    setPurgeLoading(true);
    setPurgeJobStatus(null);
    setPurgeResult(null);
    try {
      const response = await adminAPI.bulkHardDeleteExecute(
        PURGE_CONFIRM_PHRASE,
        forceRisky,
        forceRisky ? FORCE_CONFIRM_PHRASE : null
      );
      const jobId = response.data?.job_id;
      if (!jobId) {
        throw new Error('Server did not return a job id');
      }
      setPurgeJobId(jobId);
      setPurgeJobStatus({
        status: 'queued',
        progress_current: 0,
        progress_total: response.data?.total_target || 0,
        progress_message: 'Queued…',
        deleted_count: 0,
        failed_count: 0,
        skipped_count: 0,
      });
      // Begin polling immediately; UI stays on the "executing" step.
      startPollingPurgeJob(jobId);
    } catch (err) {
      const detail = err.response?.data?.detail
        || err.response?.data?.message
        || err.message
        || 'Failed to execute bulk delete';
      setPurgeResult({ success: false, error: detail });
      setPurgeStep('done');
    } finally {
      setPurgeLoading(false);
    }
  };


  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'inactive':
        return <Badge className="bg-yellow-100 text-yellow-800">Inactive</Badge>;
      case 'deleted':
        return <Badge className="bg-red-100 text-red-800">Deleted</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Subscription status badge with distinct colors for each status
  const getSubscriptionBadge = (statusLabel) => {
    if (!statusLabel) {
      return <span className="text-gray-400 text-xs">—</span>;
    }
    
    const badgeStyles = {
      'Trial': 'bg-gray-100 text-gray-700 border-gray-200',
      'Active': 'bg-blue-100 text-blue-800 border-blue-200',
      'Past Due': 'bg-orange-100 text-orange-800 border-orange-200',
      'Paused': 'bg-purple-100 text-purple-800 border-purple-200',
      'Incomplete': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Canceled': 'bg-red-100 text-red-700 border-red-200',
      'Expired': 'bg-red-50 text-red-600 border-red-200',
    };
    
    const style = badgeStyles[statusLabel] || 'bg-gray-100 text-gray-600 border-gray-200';
    return <Badge variant="outline" className={`${style} text-[10px] px-1 py-0`} data-testid="subscription-badge">{statusLabel}</Badge>;
  };

  // Format cents to dollars
  const formatAmount = (cents) => {
    if (!cents && cents !== 0) return null;
    return `$${(cents / 100).toFixed(0)}`;
  };

  // Health dot color mapping
  const healthDotClass = (status) => {
    if (status === 'healthy') return 'bg-green-500';
    if (status === 'stale') return 'bg-amber-500';
    return 'bg-gray-300';
  };

  // Readable labels for meaningful event types
  const eventLabel = (t) => ({
    po_created: 'PO Created', item_created: 'Item Created', vehicle_created: 'Vehicle Created',
    user_created: 'Member Added', use: 'Inventory Use', restock: 'Restock', adjust: 'Adjustment',
    transfer_out: 'Transfer Out', transfer_in: 'Transfer In', import_new: 'Import',
    import_merge: 'Import (Merge)', import_replace: 'Import (Replace)',
    manual_inventory_adjustment: 'Manual Adjust', audit_adjustment: 'Audit Adjust',
    transfer_vehicle_to_warehouse_approved: 'V→W Transfer',
    transfer_vehicle_to_vehicle_approved: 'V→V Transfer',
  })[t] || t || '—';

  const getActionDialogContent = () => {
    const { type, tenant } = actionDialog;
    if (!tenant) return null;

    const configs = {
      'deactivate': {
        title: 'Deactivate Tenant',
        description: `Are you sure you want to deactivate "${tenant.name}"? Users will not be able to log in or access the API.`,
        confirmText: 'Deactivate',
        confirmClass: 'bg-yellow-600 hover:bg-yellow-700'
      },
      'activate': {
        title: 'Activate Tenant',
        description: `Are you sure you want to activate "${tenant.name}"? Users will be able to log in and access the system.`,
        confirmText: 'Activate',
        confirmClass: 'bg-green-600 hover:bg-green-700'
      },
      'delete': {
        title: 'Delete Tenant',
        description: `Are you sure you want to delete "${tenant.name}"? This is a soft delete - data will be preserved but the tenant will be inaccessible.`,
        confirmText: 'Delete',
        confirmClass: 'bg-red-600 hover:bg-red-700'
      },
      'restore': {
        title: 'Restore Tenant',
        description: `Are you sure you want to restore "${tenant.name}"? The tenant will be reactivated.`,
        confirmText: 'Restore',
        confirmClass: 'bg-blue-600 hover:bg-blue-700'
      },
      'reset-password': {
        title: 'Reset Owner Password',
        description: `This will generate a temporary password for the owner of "${tenant.name}" (${tenant.owner?.email}). The user will be required to change their password on next login.`,
        confirmText: 'Reset Password',
        confirmClass: 'bg-orange-600 hover:bg-orange-700'
      }
    };

    const config = configs[type];
    if (!config) return null;

    return (
      <>
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>
        
        {actionResult && (
          <div className={`p-3 rounded-md ${actionResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {actionResult.success ? (
              type === 'reset-password' && actionResult.data ? (
                <div className="space-y-2">
                  <p className="font-medium flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Password Reset Successful
                  </p>
                  <div className="bg-card p-3 rounded border">
                    <p className="text-sm text-gray-600">Temporary Password:</p>
                    <p className="font-mono text-lg font-bold">{actionResult.data.temporary_password}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Share this with the user. They must change it on next login.
                    </p>
                  </div>
                </div>
              ) : (
                <p className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  {actionResult.message}
                </p>
              )
            ) : (
              <p className="flex items-center gap-2">
                <XCircle className="h-4 w-4" />
                {actionResult.message}
              </p>
            )}
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={closeActionDialog} disabled={actionLoading}>
            {actionResult?.success && type === 'reset-password' ? 'Close' : 'Cancel'}
          </Button>
          {!(actionResult?.success && type === 'reset-password') && (
            <Button 
              className={config.confirmClass}
              onClick={executeAction}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {config.confirmText}
            </Button>
          )}
        </DialogFooter>
      </>
    );
  };

  if (loading && tenants.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // ---- Billing Override Handler ----
  const handleBillingOverride = async () => {
    const tenant = billingOverrideModal.tenant;
    if (!tenant) return;
    setBillingOverrideLoading(true);
    try {
      const payload = billingOverrideForm.mode === 'comped'
        ? { 
            billing_mode: 'comped', 
            comped_plan: billingOverrideForm.plan, 
            reason: billingOverrideForm.reason || undefined,
            comped_expires_at: billingOverrideForm.expires_at ? `${billingOverrideForm.expires_at}T23:59:59Z` : null
          }
        : { billing_mode: 'stripe' };
      await adminAPI.billingOverride(tenant.id, payload);
      toast.success(billingOverrideForm.mode === 'comped'
        ? `Set ${tenant.name} to Comped (${billingOverrideForm.plan})`
        : `Reverted ${tenant.name} to Stripe billing`);
      setBillingOverrideModal({ open: false, tenant: null });
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Billing override failed');
    } finally {
      setBillingOverrideLoading(false);
    }
  };

  // ---- Create Comped Tenant Handler ----
  const handleCompedCreate = async () => {
    setCompedCreateLoading(true);
    try {
      const res = await adminAPI.createCompedTenant({
        tenant_name: compedCreateForm.tenant_name,
        owner_name: compedCreateForm.owner_name,
        owner_email: compedCreateForm.owner_email,
        comped_plan: compedCreateForm.comped_plan,
        product_family: compedCreateForm.product_family,
        reason: compedCreateForm.reason || undefined,
      });
      toast.success(`Created comped tenant "${res.data.tenant_name}" — temp password: ${res.data.temp_password}`, { duration: 15000 });
      setCompedCreateModal(false);
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create comped tenant');
    } finally {
      setCompedCreateLoading(false);
    }
  };


  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Header with summary strip */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold text-foreground">Tenants</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {total} total &middot; <span className="text-green-600">{tenantStats.active_count ?? 0} active</span>
              {tenantStats.comped_count > 0 && <> &middot; <span className="text-violet-600">{tenantStats.comped_count} comped</span></>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchTenants(); }} data-testid="tenants-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
            <div className="flex flex-col gap-3">
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by tenant name or owner email..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button type="submit">Search</Button>
              </form>
              <div className="flex flex-wrap gap-2">
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-full sm:w-[150px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="deleted">Deleted</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={tourFilter} onValueChange={(v) => { setTourFilter(v); setPage(1); }} data-testid="tour-status-filter">
                  <SelectTrigger className="w-full sm:w-[170px]" data-testid="tour-status-filter-trigger">
                    <SelectValue placeholder="Filter by tour" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tours</SelectItem>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={healthFilter} onValueChange={(v) => { setHealthFilter(v); setPage(1); }} data-testid="health-filter">
                  <SelectTrigger className="w-full sm:w-[160px]" data-testid="health-filter-trigger">
                    <SelectValue placeholder="Health" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Health</SelectItem>
                    <SelectItem value="healthy">Healthy</SelectItem>
                    <SelectItem value="stale">Stale</SelectItem>
                    <SelectItem value="not_activated">Not Activated</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2 px-2 py-1 border rounded-md bg-background" data-testid="hide-test-toggle">
                  <Switch
                    checked={hideTestTenants}
                    onCheckedChange={(v) => { setHideTestTenants(v); setPage(1); }}
                    className="scale-90"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">Hide test</span>
                </div>
                <Button variant="outline" onClick={fetchTenants} className="shrink-0">
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setCompedCreateForm({ tenant_name: '', owner_name: '', owner_email: '', comped_plan: 'starter', reason: '' });
                    setCompedCreateModal(true);
                  }}
                  className="gap-2 border-violet-300 text-violet-700 hover:bg-violet-50 w-full sm:w-auto"
                  data-testid="create-comped-tenant-btn"
                >
                  <Sparkles className="h-4 w-4" />
                  <span className="hidden sm:inline">Create Comped Tenant</span>
                  <span className="sm:hidden">Comped Tenant</span>
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={openPurgeModal}
                  className="gap-2 w-full sm:w-auto"
                  data-testid="purge-test-accounts-btn"
                >
                  <Flame className="h-4 w-4" />
                  <span className="hidden sm:inline">Purge Test Accounts</span>
                  <span className="sm:hidden">Purge Tests</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tenants Table */}
        <Card>
          <CardHeader className="py-4">
            <CardTitle className="text-base">Tenants ({total})</CardTitle>
          </CardHeader>
          <CardContent className="px-0 sm:px-6">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]" data-testid="tenants-table">
                <thead>
                  <tr className="border-b text-xs">
                    <th className="text-left py-2 px-2 sm:px-3 font-medium text-gray-500">Tenant</th>
                    <th className="text-left py-2 px-2 sm:px-3 font-medium text-gray-500">Status</th>
                    <th className="text-left py-2 px-2 sm:px-3 font-medium text-gray-500 hidden md:table-cell">Created</th>
                    <th className="text-left py-2 px-2 sm:px-3 font-medium text-gray-500">Drip</th>
                    <th className="text-left py-2 px-2 sm:px-3 font-medium text-gray-500 hidden sm:table-cell">Tour</th>
                    <th className="text-left py-2 px-2 sm:px-3 font-medium text-gray-500 hidden lg:table-cell">Stats</th>
                    <th className="text-right py-2 px-2 sm:px-3 font-medium text-gray-500 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((tenant) => (
                    <tr key={tenant.id} className="border-b hover:bg-gray-50/60">
                      {/* Tenant + Owner + Health Dot */}
                      <td className="py-2 px-2 sm:px-3">
                        <div className="flex items-start gap-1.5 min-w-0">
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 mt-[5px] cursor-help ${healthDotClass(tenant.health_status)}`}
                                  data-testid={`health-dot-${tenant.id}`}
                                />
                              </TooltipTrigger>
                              <TooltipContent side="right" className="text-xs max-w-[260px] p-3" data-testid={`health-tooltip-${tenant.id}`}>
                                <p className="font-semibold mb-1.5">
                                  {tenant.health_status === 'healthy' ? 'Healthy' : tenant.health_status === 'stale' ? 'Stale' : 'Not Activated'}
                                </p>
                                <div className="space-y-0.5 mb-2">
                                  <p>{(tenant.counts?.vans || 0) >= 1 ? '✓' : '○'} Vehicles {tenant.counts?.vans || 0}/1</p>
                                  <p>{(tenant.counts?.items || 0) >= 25 ? '✓' : '○'} Items {tenant.counts?.items || 0}/25</p>
                                  <p>{(tenant.counts?.users || 0) >= 1 ? '✓' : '○'} Team {tenant.counts?.users || 0}/1</p>
                                </div>
                                {tenant.last_meaningful_event_at ? (
                                  <p className="text-gray-500 mb-1">
                                    Last: {eventLabel(tenant.last_meaningful_event_type)} · {formatDistanceToNow(new Date(tenant.last_meaningful_event_at), { addSuffix: true })}
                                  </p>
                                ) : (
                                  <p className="text-gray-400 mb-1">No activity recorded</p>
                                )}
                                <p className="text-gray-500">
                                  48h activation: {tenant.first_action_within_48h
                                    ? `Yes (${tenant.hours_since_signup}h after signup)`
                                    : 'No'}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <button
                            onClick={() => openTenantDetail(tenant)}
                            className="text-left hover:text-blue-600 min-w-0"
                            data-testid={`tenant-row-${tenant.id}`}
                          >
                            <p className="font-medium text-sm leading-tight truncate max-w-[130px] sm:max-w-[230px]">
                              {tenant.name}
                              {tenant.is_test && (
                                <Badge variant="outline" className="ml-1.5 text-[9px] px-1 py-0 bg-orange-50 text-orange-600 border-orange-200 align-middle" data-testid={`test-badge-${tenant.id}`}>TEST</Badge>
                              )}
                            </p>
                            {tenant.owner ? (
                              <p className="text-xs text-gray-400 truncate max-w-[130px] sm:max-w-[230px]">
                                {tenant.owner.name} · {tenant.owner.email}
                              </p>
                            ) : (
                              <p className="text-xs text-gray-300">No owner</p>
                            )}
                            {tenant.billing_mode === 'comped' && (
                              <p className="text-[10px] text-violet-500 truncate max-w-[130px] sm:max-w-[230px]" data-testid={`comped-by-${tenant.id}`}>
                                Comped by {tenant.comped_by_admin_name || 'unknown admin'}
                                {tenant.comped_at && <> on {format(new Date(tenant.comped_at), 'MMM d, yyyy')}</>}
                              </p>
                            )}
                          </button>
                        </div>
                      </td>
                      {/* Status + Subscription merged */}
                      <td className="py-2 px-2 sm:px-3">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1 flex-wrap">
                            {getStatusBadge(tenant.status)}
                            {tenant.billing_mode === 'comped' ? (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 bg-violet-50 text-violet-600 border-violet-200" data-testid="billing-mode-comped">Comped</Badge>
                            ) : devBadgeOverride === tenant.id ? (
                              getSubscriptionBadge('Active')
                            ) : tenant.subscription_status_label ? (
                              getSubscriptionBadge(tenant.subscription_status_label)
                            ) : null}
                            {devBadgeOverride === tenant.id && (
                              <button onClick={() => setDevBadgeOverride(null)} className="text-[9px] text-red-400 hover:text-red-600 underline" data-testid="dev-badge-revert">revert</button>
                            )}
                          </div>
                          {tenant.billing_mode !== 'comped' && tenant.subscription_amount_cents != null && (
                            <span className="text-[10px] text-gray-400">{formatAmount(tenant.subscription_amount_cents)}/mo</span>
                          )}
                        </div>
                      </td>
                      {/* Created - hidden on mobile */}
                      <td className="py-2 px-2 sm:px-3 hidden md:table-cell">
                        {tenant.created_at ? (
                          <div>
                            <p className="text-xs text-gray-600">{format(new Date(tenant.created_at), 'MMM d, yyyy')}</p>
                            <p className="text-[10px] text-gray-400">
                              {formatDistanceToNow(new Date(tenant.created_at), { addSuffix: true })}
                            </p>
                          </div>
                        ) : <span className="text-xs text-gray-300">—</span>}
                      </td>
                      {/* Drip */}
                      <td className="py-2 px-2 sm:px-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); openDripDrawer(tenant.id, tenant.name); }}
                          className="text-left hover:bg-blue-50 rounded px-1 py-0.5 -mx-1 transition-colors"
                          data-testid={`drip-progress-${tenant.id}`}
                        >
                          <p className="text-xs font-medium text-foreground">
                            {tenant.drip_emails_sent || 0}/7
                          </p>
                          {tenant.last_email ? (
                            <p className="text-[10px] text-gray-500 truncate max-w-[70px] sm:max-w-[90px]">
                              {tenant.last_email.email_type?.replace('trial_day_', 'D').replace('_reengagement', '(re)')}
                              {' '}
                              <span className={tenant.last_email.success ? 'text-green-600' : 'text-red-500'}>
                                {tenant.last_email.success ? 'OK' : 'Fail'}
                              </span>
                            </p>
                          ) : (
                            <p className="text-[10px] text-gray-300">—</p>
                          )}
                        </button>
                      </td>
                      {/* Tour - hidden on mobile */}
                      <td className="py-2 px-2 sm:px-3 hidden sm:table-cell" data-testid={`tour-status-${tenant.id}`}>
                        {tenant.tour_status === 'completed' ? (
                          <Badge className="bg-green-100 text-green-800 text-[10px] px-1.5 py-0">Done</Badge>
                        ) : tenant.tour_status === 'dismissed' ? (
                          <Badge className="bg-gray-100 text-gray-500 text-[10px] px-1.5 py-0">Skip</Badge>
                        ) : tenant.tour_status === 'in_progress' ? (
                          <span className="text-xs font-medium text-blue-700">
                            {tenant.tour_steps_completed}/{tenant.tour_total_steps}
                          </span>
                        ) : (
                          <span className="text-[10px] text-gray-300">—</span>
                        )}
                      </td>
                      {/* Stats - hidden on small screens */}
                      <td className="py-2 px-2 sm:px-3 hidden lg:table-cell">
                        <div className="flex gap-2.5 text-xs text-gray-500">
                          <span className="flex items-center gap-0.5" data-testid="stat-users" title="Team members">
                            <Users className="h-3 w-3" /> {tenant.counts?.users || 0}
                          </span>
                          <span className="flex items-center gap-0.5" data-testid="stat-vehicles" title="Vehicles">
                            <Truck className="h-3 w-3" /> {tenant.counts?.vans || 0}
                          </span>
                          <span className="flex items-center gap-0.5" data-testid="stat-items" title="Parts">
                            <Package className="h-3 w-3" /> {tenant.counts?.items || 0}
                          </span>
                        </div>
                      </td>
                      {/* Actions - dropdown */}
                      <td className="py-2 px-2 sm:px-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`tenant-actions-${tenant.id}`} onClick={(e) => e.stopPropagation()}>
                              <MoreVertical className="h-4 w-4 text-gray-400" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => openTenantDetail(tenant)} data-testid="action-view-detail">
                              <Eye className="h-3.5 w-3.5 mr-2" /> View Details
                            </DropdownMenuItem>
                            {isPrimaryAdmin && tenant.status !== 'deleted' && (
                              <DropdownMenuItem onClick={() => openImpersonateModal(tenant)} disabled={isImpersonating} className="text-purple-600" data-testid="action-impersonate">
                                <UserCog className="h-3.5 w-3.5 mr-2" /> Impersonate
                              </DropdownMenuItem>
                            )}
                            {isPrimaryAdmin && tenant.status !== 'deleted' && (
                              <DropdownMenuItem onClick={() => openScreenShareModal(tenant)} className="text-blue-600" data-testid="action-screen-share">
                                <Monitor className="h-3.5 w-3.5 mr-2" /> Screen Share
                              </DropdownMenuItem>
                            )}
                            {isPrimaryAdmin && tenant.status !== 'deleted' && (
                              <DropdownMenuItem onClick={() => {
                                setBillingOverrideForm({
                                  mode: tenant.billing_mode === 'comped' ? 'comped' : 'comped',
                                  plan: tenant.comped_plan || 'starter',
                                  reason: '',
                                  expires_at: tenant.comped_expires_at ? tenant.comped_expires_at.split('T')[0] : ''
                                });
                                setBillingOverrideModal({ open: true, tenant });
                              }} className="text-violet-600" data-testid="action-billing-override">
                                <DollarSign className="h-3.5 w-3.5 mr-2" /> Billing Override
                              </DropdownMenuItem>
                            )}
                            {isPrimaryAdmin && <DropdownMenuSeparator />}
                            {isPrimaryAdmin && tenant.owner && tenant.status !== 'deleted' && (
                              <DropdownMenuItem onClick={() => openActionDialog('reset-password', tenant)} className="text-orange-600" data-testid="action-reset-password">
                                <KeyRound className="h-3.5 w-3.5 mr-2" /> Reset Password
                              </DropdownMenuItem>
                            )}
                            {isPrimaryAdmin && tenant.status === 'active' && (
                              <DropdownMenuItem onClick={() => openActionDialog('deactivate', tenant)} className="text-yellow-600" data-testid="action-deactivate">
                                <PowerOff className="h-3.5 w-3.5 mr-2" /> Deactivate
                              </DropdownMenuItem>
                            )}
                            {isPrimaryAdmin && tenant.status === 'inactive' && (
                              <DropdownMenuItem onClick={() => openActionDialog('activate', tenant)} className="text-green-600" data-testid="action-activate">
                                <Power className="h-3.5 w-3.5 mr-2" /> Activate
                              </DropdownMenuItem>
                            )}
                            {isPrimaryAdmin && tenant.status === 'deleted' && (
                              <DropdownMenuItem onClick={() => openActionDialog('restore', tenant)} className="text-blue-600" data-testid="action-restore">
                                <RotateCcw className="h-3.5 w-3.5 mr-2" /> Restore
                              </DropdownMenuItem>
                            )}
                            {isPrimaryAdmin && tenant.status !== 'deleted' && (
                              <DropdownMenuItem onClick={() => openActionDialog('delete', tenant)} className="text-red-600" data-testid="action-soft-delete">
                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                              </DropdownMenuItem>
                            )}
                            {isPrimaryAdmin && <DropdownMenuItem onClick={() => openTenantPurgeModal(tenant)} className="text-red-800" data-testid="action-purge">
                              <AlertOctagon className="h-3.5 w-3.5 mr-2" /> Purge (Dev)
                            </DropdownMenuItem>}
                            {isPrimaryAdmin && <DropdownMenuSeparator />}
                            {isPrimaryAdmin && <DropdownMenuItem
                              onClick={() => setDevBadgeOverride(devBadgeOverride === tenant.id ? null : tenant.id)}
                              className="text-gray-500 text-[11px]"
                              data-testid="action-dev-force-badge"
                            >
                              <CreditCard className="h-3.5 w-3.5 mr-2" />
                              {devBadgeOverride === tenant.id ? 'Revert Badge' : 'Force "Active" Badge'}
                            </DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600">
                  Page {page} of {pages} ({total} total)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(pages, p + 1))}
                    disabled={page === pages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tenant Detail Dialog */}
      <Dialog open={!!selectedTenant} onOpenChange={(open) => !open && closeTenantDetail()}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {selectedTenant?.name}
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : tenantDetail ? (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center gap-4">
                {getStatusBadge(tenantDetail.status)}
                {tenantDetail.deleted_at && (
                  <span className="text-sm text-gray-500">
                    Deleted {formatDistanceToNow(new Date(tenantDetail.deleted_at), { addSuffix: true })}
                  </span>
                )}
              </div>
              
              {/* Founder Status (Read-only) */}
              {tenantDetail.is_founder && (
                <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-3">
                    <Sparkles className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">Founding Member</p>
                      <p className="text-sm text-amber-600">$10/month discount forever (automatic, first 100 signups)</p>
                    </div>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700">Founder</Badge>
                </div>
              )}
              
              {/* Beta Customer Toggle */}
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-3">
                  <Sparkles className={`h-5 w-5 ${tenantDetail.is_beta ? 'text-purple-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium">Beta Customer</p>
                    <p className="text-sm text-gray-500">$10/month discount forever</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {betaToggleLoading && <Loader2 className="h-4 w-4 animate-spin text-purple-600" />}
                  <Switch
                    checked={tenantDetail.is_beta || false}
                    onCheckedChange={(checked) => handleBetaToggle(tenantDetail.id, checked)}
                    disabled={betaToggleLoading || tenantDetail.status === 'deleted'}
                  />
                </div>
              </div>
              
              {/* Comped Billing Info (only for comped tenants) */}
              {tenantDetail.billing_mode === 'comped' && (
                <div className="flex items-center justify-between p-4 bg-violet-50 rounded-lg border border-violet-200" data-testid="comped-audit-info">
                  <div className="flex items-center gap-3">
                    <DollarSign className="h-5 w-5 text-violet-600" />
                    <div>
                      <p className="font-medium text-violet-800">
                        Comped — {tenantDetail.comped_plan ? tenantDetail.comped_plan.charAt(0).toUpperCase() + tenantDetail.comped_plan.slice(1) : 'Unknown'} Plan
                      </p>
                      <p className="text-sm text-violet-600">
                        {tenantDetail.comped_by_admin_name
                          ? `By ${tenantDetail.comped_by_admin_name}`
                          : 'By unknown admin'}
                        {tenantDetail.comped_at && ` on ${format(new Date(tenantDetail.comped_at), 'MMM d, yyyy')}`}
                      </p>
                      {tenantDetail.comped_reason && (
                        <p className="text-xs text-violet-500 mt-0.5">Reason: {tenantDetail.comped_reason}</p>
                      )}
                      {tenantDetail.comped_expires_at && (
                        <p className="text-xs text-violet-500 mt-0.5">Expires: {format(new Date(tenantDetail.comped_expires_at), 'MMM d, yyyy')}</p>
                      )}
                    </div>
                  </div>
                  <Badge className="bg-violet-100 text-violet-700 border-violet-200" variant="outline">Comped</Badge>
                </div>
              )}

              {/* Add-on Bypass Toggle (for comped/test tenants) */}
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200" data-testid="addon-bypass-toggle">
                <div className="flex items-center gap-3">
                  <Shield className={`h-5 w-5 ${tenantDetail.allow_addons_without_subscription ? 'text-amber-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium">Add-on Testing Override</p>
                    <p className="text-sm text-gray-500">Allow add-ons without active subscription</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {addonBypassLoading && <Loader2 className="h-4 w-4 animate-spin text-amber-600" />}
                  <Switch
                    checked={tenantDetail.allow_addons_without_subscription || false}
                    onCheckedChange={(checked) => handleAddonBypassToggle(tenantDetail.id, checked)}
                    disabled={!isPrimaryAdmin || addonBypassLoading || tenantDetail.status === 'deleted'}
                    data-testid="addon-bypass-switch"
                  />
                </div>
              </div>

              {/* CRM Beta / Trial Toggle */}
              <div className={`p-4 rounded-lg border ${
                tenantDetail.crm_beta_status === 'active' ? 'bg-blue-50 border-blue-200' 
                : tenantDetail.crm_beta_status === 'expired' ? 'bg-gray-50 border-gray-200' 
                : 'bg-gray-50 border-gray-200'
              }`} data-testid="crm-beta-toggle">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Zap className={`h-5 w-5 ${
                      tenantDetail.crm_beta_status === 'active' ? 'text-blue-600' 
                      : 'text-gray-400'
                    }`} />
                    <div>
                      <p className="font-medium">CRM Beta / Trial</p>
                      <p className="text-sm text-gray-500">
                        {tenantDetail.paid_crm 
                          ? 'Tenant has paid CRM — beta not needed'
                          : 'Temporary CRM access (includes Dispatch). Does not alter billing.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {tenantDetail.crm_beta_status === 'active' && (
                      <Badge className="bg-blue-100 text-blue-700 border-blue-200" variant="outline">Active</Badge>
                    )}
                    {tenantDetail.crm_beta_status === 'expired' && (
                      <Badge className="bg-gray-100 text-gray-500 border-gray-200" variant="outline">Expired</Badge>
                    )}
                    {crmBetaLoading && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
                    <Switch
                      checked={tenantDetail.crm_beta_status === 'active'}
                      onCheckedChange={(checked) => handleCrmBetaToggle(tenantDetail.id, checked)}
                      disabled={!isPrimaryAdmin || crmBetaLoading || tenantDetail.status === 'deleted' || tenantDetail.paid_crm}
                      data-testid="crm-beta-switch"
                    />
                  </div>
                </div>
                {/* Duration input — shown when toggle is off and tenant doesn't have paid CRM */}
                {tenantDetail.crm_beta_status !== 'active' && !tenantDetail.paid_crm && (
                  <div className="mt-3 flex items-center gap-2">
                    <label className="text-sm text-gray-600">Duration:</label>
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      value={crmBetaDays}
                      onChange={(e) => setCrmBetaDays(parseInt(e.target.value) || 30)}
                      className="w-20 h-8 text-sm"
                      data-testid="crm-beta-duration"
                    />
                    <span className="text-sm text-gray-500">days</span>
                  </div>
                )}
                {/* Expiration display when active */}
                {tenantDetail.crm_beta_status === 'active' && tenantDetail.crm_beta_expires_at && (
                  <div className="mt-2 text-sm text-blue-600">
                    Expires: {format(new Date(tenantDetail.crm_beta_expires_at), 'MMM d, yyyy h:mm a')}
                    {tenantDetail.crm_beta_enabled_by && (
                      <span className="text-gray-400 ml-2">
                        · Enabled by {tenantDetail.crm_beta_enabled_by}
                      </span>
                    )}
                  </div>
                )}
                {/* Show last expiration date when expired */}
                {tenantDetail.crm_beta_status === 'expired' && tenantDetail.crm_beta_expires_at && (
                  <div className="mt-2 text-sm text-gray-400">
                    Expired: {format(new Date(tenantDetail.crm_beta_expires_at), 'MMM d, yyyy h:mm a')}
                  </div>
                )}
              </div>

              {/* Business / i18n Info */}
              <div className="grid grid-cols-3 gap-4" data-testid="tenant-i18n-info">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Business Country</p>
                  <p className="text-sm font-medium">{getCountryName(tenantDetail.business_country_code || 'US')}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Currency</p>
                  <p className="text-sm font-medium">{tenantDetail.currency_code || 'USD'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Industry</p>
                  <p className="text-sm font-medium">
                    {tenantDetail.industry_code
                      ? (tenantDetail.industry_code === 'other'
                        ? (tenantDetail.industry_other_text || 'Other')
                        : getIndustryLabel(tenantDetail.industry_code))
                      : '—'}
                  </p>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{tenantDetail.counts?.users || 0}</p>
                  <p className="text-xs text-gray-500">Users</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{tenantDetail.counts?.vans || 0}</p>
                  <p className="text-xs text-gray-500">Vans</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{tenantDetail.counts?.items || 0}</p>
                  <p className="text-xs text-gray-500">Items</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{tenantDetail.counts?.inventory_rows || 0}</p>
                  <p className="text-xs text-gray-500">Inventory</p>
                </div>
              </div>
              
              {/* Users */}
              <div>
                <h3 className="font-medium mb-2">Users ({tenantDetail.users?.length || 0})</h3>
                <div className="space-y-2">
                  {tenantDetail.users?.map((user) => (
                    <div key={user.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3" data-testid={`tenant-user-row-${user.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{user.name}</p>
                        <p className="text-sm text-gray-500 truncate">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge variant={user.role === 'owner' ? 'default' : 'outline'}>
                          {user.role}
                        </Badge>
                        {!user.is_active && (
                          <Badge variant="destructive">Inactive</Badge>
                        )}
                        {user.force_password_change && (
                          <Badge className="bg-orange-100 text-orange-800">Password Reset</Badge>
                        )}
                        {isPrimaryAdmin && user.is_active && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-gray-400 hover:text-orange-600"
                                  onClick={() => handleUserPasswordReset(user)}
                                  data-testid={`reset-password-btn-${user.id}`}
                                >
                                  <KeyRound className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Reset password</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Metadata */}
              <div className="text-sm text-gray-500 border-t pt-4">
                <p>Created: {tenantDetail.created_at ? format(new Date(tenantDetail.created_at), 'PPpp') : 'Unknown'}</p>
                <p className="font-mono text-xs mt-1">ID: {tenantDetail.id}</p>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>


      {/* Per-User Password Reset Dialog */}
      <Dialog open={!!userResetState.step} onOpenChange={(open) => !open && closeUserResetDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="user-reset-dialog-title">
              {userResetState.step === 'confirm' ? 'Reset User Password' : 'Password Reset Complete'}
            </DialogTitle>
            <DialogDescription>
              {userResetState.step === 'confirm'
                ? `This will generate a temporary password for the user. They will be required to change it on next login.`
                : `Share the temporary password with the user securely.`}
            </DialogDescription>
          </DialogHeader>

          {userResetState.step === 'confirm' && userResetState.user && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-1">
                <p className="font-medium" data-testid="user-reset-name">{userResetState.user.name}</p>
                <p className="text-sm text-gray-500" data-testid="user-reset-email">{userResetState.user.email}</p>
                <Badge variant="outline" className="mt-1">{userResetState.user.role}</Badge>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={closeUserResetDialog} data-testid="user-reset-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={confirmUserPasswordReset}
                  disabled={userResetState.loading}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="user-reset-confirm-btn"
                >
                  {userResetState.loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
                  Reset Password
                </Button>
              </DialogFooter>
            </div>
          )}

          {userResetState.step === 'result' && userResetState.user && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="h-4 w-4" />
                  <span className="font-medium">Password reset for {userResetState.user.name}</span>
                </div>
                <p className="text-sm text-gray-600">{userResetState.user.email}</p>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">Temporary Password</label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={userResetState.tempPassword || ''}
                    className="font-mono text-sm"
                    data-testid="user-reset-temp-password"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(userResetState.tempPassword);
                      toast.success('Copied to clipboard');
                    }}
                    data-testid="user-reset-copy-btn"
                  >
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">User must change this on next login.</p>
              </div>
              <DialogFooter>
                <Button onClick={closeUserResetDialog} data-testid="user-reset-done-btn">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => !open && closeActionDialog()}>
        <DialogContent>
          {getActionDialogContent()}
        </DialogContent>
      </Dialog>

      {/* Purge Test Tenants Modal */}
      <Dialog open={purgeModalOpen} onOpenChange={(open) => !open && closePurgeModal()}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertOctagon className="h-5 w-5" />
              Purge Test Accounts
            </DialogTitle>
            <DialogDescription>
              Permanently delete all tenants whose owner email matches domains in the allowlist.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {/* Initial Step - Manage Domains */}
          {purgeStep === 'initial' && (
            <div className="space-y-4">
              {/* Domain Allowlist Management */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-blue-600" />
                  Test Domain Allowlist
                </h3>
                
                {/* Add Domain Input */}
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="e.g., test.com or @example.com"
                    value={newDomainInput}
                    onChange={(e) => setNewDomainInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleAddDomain} 
                    disabled={domainLoading || !newDomainInput.trim()}
                    size="sm"
                  >
                    {domainLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
                  </Button>
                </div>
                
                {/* Domain List */}
                {testDomains.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">
                    No domains configured. Add domains like test.com or example.com to enable purging.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {testDomains.map((d) => (
                      <Badge 
                        key={d.id} 
                        variant="secondary" 
                        className="flex items-center gap-1 pr-1"
                      >
                        @{d.domain}
                        <button
                          onClick={() => handleRemoveDomain(d.id)}
                          className="ml-1 hover:bg-gray-300 rounded p-0.5"
                          disabled={domainLoading}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              <Alert className="border-red-200 bg-red-50">
                <AlertOctagon className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  <strong>Warning:</strong> This will permanently delete all tenant data including users, 
                  vehicles, inventory, purchase orders, and all associated records for tenants 
                  whose owner email matches the domains above.
                </AlertDescription>
              </Alert>
              
              {/* Optional: expand scope to obvious test-pattern matches even if
                  they don't match the allowlist exactly. Useful when the DB has
                  drifted (older test runs added tenants with .test/example.com
                  emails or tenant names containing demo/test/sandbox). */}
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <input
                  type="checkbox"
                  id="forceRiskyInitial"
                  data-testid="purge-include-patterns-checkbox"
                  checked={forceRisky}
                  onChange={(e) => {
                    setForceRisky(e.target.checked);
                    if (!e.target.checked) setForceConfirmPhrase('');
                  }}
                  className="mt-1"
                />
                <div>
                  <label htmlFor="forceRiskyInitial" className="text-sm font-medium text-amber-900 cursor-pointer">
                    Also include obvious test-pattern matches
                  </label>
                  <p className="text-xs text-amber-700 leading-snug">
                    Adds tenants whose owner email is on a reserved test TLD
                    (<code>.test</code>, <code>.example</code>, <code>example.com</code>) or whose tenant name
                    contains <em>demo / test / playground / sandbox / qa-</em>. Real customer
                    domains are never included. Required for cleaning up
                    historical test fixtures that pre-date the allowlist.
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-600">
                Click &quot;Dry Run&quot; to preview which tenants will be deleted without making any changes.
              </p>
              
              <DialogFooter>
                <Button variant="outline" onClick={closePurgeModal}>Cancel</Button>
                <Button 
                  onClick={handlePurgeDryRun} 
                  disabled={purgeLoading || (testDomains.length === 0 && !forceRisky)}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="purge-dry-run-btn"
                >
                  {purgeLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Dry Run
                </Button>
              </DialogFooter>
            </div>
          )}
          
          {/* Dry Run Results */}
          {purgeStep === 'dry-run' && purgeDryRunData && (
            <div className="space-y-4">
              {/* Show allowlist domains */}
              <div className="flex flex-wrap gap-1 items-center text-sm">
                <span className="text-gray-500">Matching domains:</span>
                {purgeDryRunData.allowlist_domains?.map((d) => (
                  <Badge key={d} variant="outline" className="text-xs">@{d}</Badge>
                ))}
              </div>
              
              {purgeDryRunData.total_count === 0 ? (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    No matching tenants found. Nothing to delete.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  {/* Summary Counts */}
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-2xl font-bold text-green-600">{purgeDryRunData.safe_count || 0}</p>
                      <p className="text-xs text-green-700">SAFE</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-2xl font-bold text-red-600">{purgeDryRunData.risky_count || 0}</p>
                      <p className="text-xs text-red-700">RISKY</p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-2xl font-bold text-gray-600">{purgeDryRunData.total_count || 0}</p>
                      <p className="text-xs text-gray-700">TOTAL</p>
                    </div>
                  </div>
                  
                  {/* Risky tenant warning */}
                  {purgeDryRunData.has_risky_tenants && (
                    <Alert className="border-red-300 bg-red-50">
                      <AlertOctagon className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-800">
                        <strong>Warning:</strong> Some matched tenants appear to have real activity or billing. 
                        These will <strong>NOT</strong> be deleted unless you explicitly force delete.
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {/* Data Summary */}
                  <div className="grid grid-cols-5 gap-2 text-xs">
                    <div className="bg-gray-50 rounded p-2 text-center">
                      <p className="font-bold">{purgeDryRunData.summary?.total_users || 0}</p>
                      <p className="text-gray-500">Users</p>
                    </div>
                    <div className="bg-gray-50 rounded p-2 text-center">
                      <p className="font-bold">{purgeDryRunData.summary?.total_vehicles || 0}</p>
                      <p className="text-gray-500">Vehicles</p>
                    </div>
                    <div className="bg-gray-50 rounded p-2 text-center">
                      <p className="font-bold">{purgeDryRunData.summary?.total_items || 0}</p>
                      <p className="text-gray-500">Items</p>
                    </div>
                    <div className="bg-gray-50 rounded p-2 text-center">
                      <p className="font-bold">{purgeDryRunData.summary?.total_purchase_orders || 0}</p>
                      <p className="text-gray-500">POs</p>
                    </div>
                    <div className="bg-gray-50 rounded p-2 text-center">
                      <p className="font-bold">{purgeDryRunData.summary?.total_inventory_events || 0}</p>
                      <p className="text-gray-500">Events</p>
                    </div>
                  </div>
                  
                  {/* Tenant List with Risk Badges */}
                  <div className="max-h-56 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left py-2 px-3 font-medium">Tenant</th>
                          <th className="text-left py-2 px-3 font-medium">Owner Email</th>
                          <th className="text-left py-2 px-3 font-medium">Risk</th>
                          <th className="text-left py-2 px-3 font-medium">Reasons</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purgeDryRunData.matched_tenants.map((tenant) => (
                          <tr key={tenant.tenant_id} className={`border-t ${tenant.risk?.is_risky ? 'bg-red-50/50' : ''}`}>
                            <td className="py-2 px-3">
                              <div>{tenant.tenant_name}</div>
                              <div className="text-xs text-gray-400">
                                {tenant.created_at ? format(new Date(tenant.created_at), 'MMM d, yyyy') : '-'}
                              </div>
                            </td>
                            <td className="py-2 px-3 text-gray-500 text-xs">{tenant.owner_email}</td>
                            <td className="py-2 px-3">
                              {tenant.risk?.is_risky ? (
                                <Badge className="bg-red-100 text-red-800 border-red-300 text-xs">
                                  RISKY ({tenant.risk.risk_score})
                                </Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-800 border-green-300 text-xs">
                                  SAFE ({tenant.risk?.risk_score || 0})
                                </Badge>
                              )}
                            </td>
                            <td className="py-2 px-3 text-xs text-gray-500">
                              {tenant.risk?.risk_reasons?.length > 0 ? (
                                <ul className="list-disc list-inside">
                                  {tenant.risk.risk_reasons.map((reason, i) => (
                                    <li key={i}>{reason}</li>
                                  ))}
                                </ul>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Confirm Phase */}
                  <div className="space-y-3 pt-4 border-t">
                    {/* Force risky checkbox */}
                    {purgeDryRunData.has_risky_tenants && (
                      <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <input
                          type="checkbox"
                          id="forceRisky"
                          checked={forceRisky}
                          onChange={(e) => {
                            setForceRisky(e.target.checked);
                            if (!e.target.checked) setForceConfirmPhrase('');
                          }}
                          className="mt-1"
                        />
                        <div>
                          <label htmlFor="forceRisky" className="text-sm font-medium text-red-800 cursor-pointer">
                            Force delete risky tenants (not recommended)
                          </label>
                          <p className="text-xs text-red-600">
                            This will delete ALL matched tenants including those with billing/activity. This cannot be undone.
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Primary confirm phrase */}
                    <div>
                      <p className="text-sm font-medium mb-1">
                        Type <code className="bg-amber-100 px-2 py-0.5 rounded text-amber-800">{PURGE_CONFIRM_PHRASE}</code> to proceed:
                      </p>
                      <Input
                        data-testid="purge-confirm-phrase-input"
                        value={purgeConfirmPhrase}
                        onChange={(e) => setPurgeConfirmPhrase(e.target.value)}
                        placeholder={PURGE_CONFIRM_PHRASE}
                        className="font-mono"
                      />
                    </div>
                    
                    {/* Secondary confirm phrase for force delete */}
                    {forceRisky && (
                      <div>
                        <p className="text-sm font-medium mb-1 text-red-700">
                          Type <code className="bg-red-100 px-2 py-0.5 rounded text-red-800">{FORCE_CONFIRM_PHRASE}</code> to force delete:
                        </p>
                        <Input
                          data-testid="purge-force-confirm-phrase-input"
                          value={forceConfirmPhrase}
                          onChange={(e) => setForceConfirmPhrase(e.target.value)}
                          placeholder={FORCE_CONFIRM_PHRASE}
                          className="font-mono border-red-300"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={() => { setPurgeStep('initial'); setForceRisky(false); setForceConfirmPhrase(''); }}>Back</Button>
                <Button variant="outline" onClick={closePurgeModal}>Cancel</Button>
                {purgeDryRunData.total_count > 0 && (
                  <Button 
                    variant="destructive"
                    onClick={handlePurgeExecute}
                    data-testid="purge-execute-btn"
                    disabled={
                      purgeLoading || 
                      purgeConfirmPhrase !== PURGE_CONFIRM_PHRASE ||
                      (forceRisky && forceConfirmPhrase !== FORCE_CONFIRM_PHRASE)
                    }
                  >
                    {purgeLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Flame className="h-4 w-4 mr-2" />}
                    {forceRisky ? 'Delete ALL Permanently' : `Delete ${purgeDryRunData.safe_count} Safe Tenant(s)`}
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
          
          {/* Executing */}
          {purgeStep === 'executing' && (
            <div className="flex flex-col items-center justify-center py-6 space-y-4">
              <Loader2 className="h-10 w-10 animate-spin text-red-600" />
              <p className="text-lg font-medium">
                {purgeJobStatus?.status === 'queued' ? 'Queued…' : 'Deleting tenants…'}
              </p>
              {/* Live progress bar */}
              {purgeJobStatus && (
                <div className="w-full max-w-md space-y-3">
                  <div className="h-3 w-full rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className="h-full bg-red-600 transition-all duration-300"
                      style={{
                        width: `${
                          purgeJobStatus.progress_total
                            ? Math.min(100, Math.round((purgeJobStatus.progress_current / purgeJobStatus.progress_total) * 100))
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>
                      {purgeJobStatus.progress_current || 0} / {purgeJobStatus.progress_total || 0}
                    </span>
                    <span>
                      ✓ {purgeJobStatus.deleted_count || 0}
                      {' · '}
                      ⚠ {purgeJobStatus.skipped_count || 0}
                      {' · '}
                      ✗ {purgeJobStatus.failed_count || 0}
                    </span>
                  </div>
                  {purgeJobStatus.progress_message && (
                    <p className="text-xs text-gray-500 text-center">{purgeJobStatus.progress_message}</p>
                  )}
                  {purgeJobId && (
                    <p className="text-[10px] font-mono text-gray-400 text-center">job: {purgeJobId}</p>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-400 max-w-md text-center">
                The purge runs as a background job. You can leave this window open to watch progress, or close it and check the Audit Log later — the deletion will continue server-side.
              </p>
            </div>
          )}
          
          {/* Done */}
          {purgeStep === 'done' && purgeResult && (
            <div className="space-y-4">
              {purgeResult.error ? (
                <Alert className="border-red-200 bg-red-50">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {purgeResult.error}
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  {/* Summary counts (from job result) */}
                  {(() => {
                    const perTenant = purgeResult.result_data?.per_tenant || [];
                    const failedRows = perTenant.filter((r) => r.status === 'failed');
                    const skippedRows = perTenant.filter((r) => r.status === 'skipped');
                    const deletedRows = perTenant.filter((r) => r.status === 'deleted');
                    return (
                      <>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                            <p className="text-xl font-bold text-green-600">{purgeResult.deleted_count || 0}</p>
                            <p className="text-xs text-green-700">Deleted</p>
                          </div>
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                            <p className="text-xl font-bold text-amber-600">{purgeResult.skipped_count || 0}</p>
                            <p className="text-xs text-amber-700">Skipped</p>
                          </div>
                          <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                            <p className="text-xl font-bold text-red-600">{purgeResult.failed_count || 0}</p>
                            <p className="text-xs text-red-700">Failed</p>
                          </div>
                        </div>

                        <Alert className={purgeResult.failed_count > 0
                          ? "border-amber-200 bg-amber-50"
                          : "border-green-200 bg-green-50"
                        }>
                          {purgeResult.failed_count > 0 ? (
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                          ) : (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          <AlertDescription className={purgeResult.failed_count > 0 ? "text-amber-800" : "text-green-800"}>
                            Purge complete. Deleted <strong>{purgeResult.deleted_count || 0}</strong> tenant(s).
                            {purgeResult.skipped_count > 0 && (
                              <> Skipped <strong>{purgeResult.skipped_count}</strong> tenant(s) (risky / missing).</>
                            )}
                            {purgeResult.failed_count > 0 && (
                              <> Failed <strong>{purgeResult.failed_count}</strong> tenant(s) — see the error list below.</>
                            )}
                          </AlertDescription>
                        </Alert>

                        {/* Failed tenants */}
                        {failedRows.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-red-700 mb-2">Failed Tenants:</p>
                            <div className="max-h-40 overflow-y-auto border border-red-200 rounded-lg bg-red-50/50">
                              <table className="w-full text-sm">
                                <tbody>
                                  {failedRows.map((row) => (
                                    <tr key={row.tenant_id} className="border-b border-red-100 last:border-0">
                                      <td className="py-2 px-3 align-top">{row.tenant_name || row.tenant_id}</td>
                                      <td className="py-2 px-3 align-top text-xs text-gray-500">{row.owner_email}</td>
                                      <td className="py-2 px-3 align-top text-xs text-red-700 font-mono break-all">{row.error}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Skipped tenants */}
                        {skippedRows.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-amber-700 mb-2">Skipped Tenants:</p>
                            <div className="max-h-32 overflow-y-auto border border-amber-200 rounded-lg bg-amber-50/50">
                              <table className="w-full text-sm">
                                <tbody>
                                  {skippedRows.map((row) => (
                                    <tr key={row.tenant_id} className="border-b border-amber-100 last:border-0">
                                      <td className="py-2 px-3">{row.tenant_name || row.tenant_id}</td>
                                      <td className="py-2 px-3 text-xs text-gray-500">{row.owner_email}</td>
                                      <td className="py-2 px-3 text-xs text-amber-700">
                                        {row.reason}{row.risk_score != null ? ` (score: ${row.risk_score})` : ''}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Deleted tenants */}
                        {deletedRows.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-green-700 mb-2">Deleted Tenants:</p>
                            <div className="max-h-32 overflow-y-auto border border-green-200 rounded-lg bg-green-50/50">
                              <table className="w-full text-sm">
                                <tbody>
                                  {deletedRows.map((row) => (
                                    <tr key={row.tenant_id} className="border-b border-green-100 last:border-0">
                                      <td className="py-2 px-3">{row.tenant_name || row.tenant_id}</td>
                                      <td className="py-2 px-3 text-xs text-gray-500">{row.owner_email}</td>
                                      <td className="py-2 px-3">
                                        <Badge className="bg-green-100 text-green-800 text-xs">
                                          {row.risk_score >= 40 ? 'Force Deleted' : 'Deleted'}
                                        </Badge>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
              
              <DialogFooter>
                <Button onClick={closePurgeModal}>Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Impersonate Tenant Modal */}
      <Dialog open={impersonateModal.open} onOpenChange={(open) => !open && closeImpersonateModal()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-purple-600" />
              Impersonate Tenant
            </DialogTitle>
            <DialogDescription>
              You are about to view the application as this tenant. All actions will be logged.
            </DialogDescription>
          </DialogHeader>
          
          {impersonateModal.tenant && (
            <div className="space-y-4">
              {/* Tenant Info */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Building2 className="h-8 w-8 text-purple-600" />
                  <div>
                    <p className="font-semibold text-lg">{impersonateModal.tenant.name}</p>
                    <p className="text-sm text-gray-500">{impersonateModal.tenant.owner?.email}</p>
                  </div>
                </div>
              </div>

              {/* Read-only option */}
              <div className="flex items-start space-x-3 p-3 border rounded-lg bg-amber-50/50 border-amber-200">
                <Checkbox
                  id="readOnly"
                  checked={impersonateReadOnly}
                  onCheckedChange={setImpersonateReadOnly}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <label 
                    htmlFor="readOnly" 
                    className="font-medium text-sm cursor-pointer flex items-center gap-2"
                  >
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    Read-only mode
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Prevents all data modifications. Recommended for viewing only.
                  </p>
                </div>
              </div>

              {/* Warning */}
              <Alert className="border-purple-200 bg-purple-50/50">
                <Shield className="h-4 w-4 text-purple-600" />
                <AlertDescription className="text-purple-800 text-sm">
                  <strong>Security Notice:</strong> This session will expire in 30 minutes. 
                  All actions are logged for audit purposes. A banner will be shown to indicate
                  you are impersonating this tenant.
                </AlertDescription>
              </Alert>

              {/* Error message */}
              {impersonateError && (
                <Alert className="border-red-200 bg-red-50">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {impersonateError}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={closeImpersonateModal} disabled={impersonateLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleStartImpersonation}
              disabled={impersonateLoading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {impersonateLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <UserCog className="h-4 w-4 mr-2" />
                  Start Impersonation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Screen Share Modal */}
      <Dialog open={screenShareModal.open} onOpenChange={(open) => !open && closeScreenShareModal()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Monitor className="h-5 w-5 text-blue-600" />
              Request Screen Share
            </DialogTitle>
            <DialogDescription>
              Request to view the tenant&apos;s screen for support purposes.
            </DialogDescription>
          </DialogHeader>
          
          {screenShareModal.tenant && (
            <div className="space-y-4">
              {/* Tenant Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Building2 className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="font-semibold text-lg">{screenShareModal.tenant.name}</p>
                    <p className="text-sm text-gray-500">{screenShareModal.tenant.owner?.email}</p>
                  </div>
                </div>
              </div>

              {/* How it works */}
              <div className="space-y-2">
                <p className="text-sm font-medium">How it works:</p>
                <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                  <li>A session will be created with a unique invite link</li>
                  <li>Share the link with the tenant or wait for them to see the in-app notification</li>
                  <li>The tenant must explicitly accept and choose to share their browser tab</li>
                  <li>The session automatically expires after 15 minutes</li>
                </ul>
              </div>

              {/* Privacy notice */}
              <Alert className="border-blue-200 bg-blue-50/50">
                <Shield className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  <strong>Privacy-first:</strong> The tenant will only share their Stock & Load browser tab, 
                  not their entire screen. All sessions are logged for audit purposes.
                </AlertDescription>
              </Alert>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={closeScreenShareModal} disabled={screenShareLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleStartScreenShare}
              disabled={screenShareLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {screenShareLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Session...
                </>
              ) : (
                <>
                  <Monitor className="h-4 w-4 mr-2" />
                  Start Session
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Screen Share Viewer */}
      <AdminScreenShareViewer
        isOpen={screenShareViewerOpen}
        onClose={() => setScreenShareViewerOpen(false)}
        sessionData={screenShareSession}
        onSessionEnd={handleEndScreenShare}
      />

      {/* DEV-ONLY: Single Tenant Purge Modal */}
      <Dialog open={tenantPurgeModal.open} onOpenChange={(open) => !open && closeTenantPurgeModal()}>
        <DialogContent 
          className="max-w-xl"
          onCloseAutoFocus={(event) => {
            // Prevent Radix from leaving pointer-events: none on body
            event.preventDefault();
            document.body.style.pointerEvents = '';
            document.documentElement.style.pointerEvents = '';
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertOctagon className="h-5 w-5" />
              Permanently Delete Tenant (Dev Only)
            </DialogTitle>
            <DialogDescription>
              This will permanently remove the tenant and ALL associated data from the database.
            </DialogDescription>
          </DialogHeader>
          
          {tenantPurgeLoading && tenantPurgeModal.step === 'preview' && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
            </div>
          )}
          
          {tenantPurgeModal.step === 'disabled' && (
            <div className="space-y-4">
              <Alert className="border-yellow-500 bg-yellow-50">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  <strong>Tenant purge is disabled in this environment.</strong>
                  <p className="mt-1 text-sm">
                    To enable, set <code className="bg-yellow-100 px-1 rounded">ALLOW_TENANT_PURGE=true</code> in the backend environment variables.
                  </p>
                </AlertDescription>
              </Alert>
              <DialogFooter>
                <Button variant="outline" onClick={closeTenantPurgeModal}>Close</Button>
              </DialogFooter>
            </div>
          )}
          
          {tenantPurgeModal.step === 'error' && (
            <div className="space-y-4">
              <Alert className="border-red-500 bg-red-50">
                <XCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {tenantPurgeModal.preview?.error || 'An error occurred'}
                </AlertDescription>
              </Alert>
              <DialogFooter>
                <Button variant="outline" onClick={closeTenantPurgeModal}>Close</Button>
              </DialogFooter>
            </div>
          )}
          
          {tenantPurgeModal.step === 'confirm' && tenantPurgeModal.preview && (
            <div className="space-y-4">
              {/* Danger Zone Warning */}
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
                  <AlertTriangle className="h-5 w-5" />
                  DANGER ZONE - Irreversible Action
                </div>
                <p className="text-sm text-red-600">
                  You are about to permanently delete <strong>{tenantPurgeModal.preview.tenant_name}</strong> and all associated data.
                  This action <strong>cannot be undone</strong>.
                </p>
              </div>
              
              {/* Tenant Info */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Tenant ID:</span> <span className="font-mono">{tenantPurgeModal.tenant?.id}</span></div>
                  <div><span className="text-gray-500">Status:</span> <Badge variant={tenantPurgeModal.preview.status === 'active' ? 'default' : 'secondary'}>{tenantPurgeModal.preview.status}</Badge></div>
                  <div><span className="text-gray-500">Owner:</span> {tenantPurgeModal.preview.owner_email || 'N/A'}</div>
                  <div><span className="text-gray-500">Created:</span> {tenantPurgeModal.preview.created_at ? format(new Date(tenantPurgeModal.preview.created_at), 'MMM d, yyyy') : 'N/A'}</div>
                </div>
              </div>
              
              {/* Record counts */}
              <div className="border rounded-lg p-3">
                <h4 className="font-medium text-sm mb-2">Records to be deleted ({tenantPurgeModal.preview.total_records} total):</h4>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  {Object.entries(tenantPurgeModal.preview.record_counts || {}).map(([table, count]) => (
                    <div key={table} className="flex justify-between bg-gray-50 rounded px-2 py-1">
                      <span className="text-gray-600">{table}:</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
                {tenantPurgeModal.preview.warnings?.length > 0 && (
                  <p data-testid="purge-preview-warnings" className="text-xs text-amber-600 mt-2">
                    Preview incomplete for {tenantPurgeModal.preview.warnings.length} table(s). Purge may still succeed.
                  </p>
                )}
              </div>
              
              {/* Confirmation Form */}
              <div className="space-y-4 pt-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type <span className="font-mono bg-gray-100 px-1 rounded">{tenantPurgeModal.preview.tenant_name}</span> to confirm:
                  </label>
                  <Input
                    value={tenantPurgeForm.nameConfirm}
                    onChange={(e) => setTenantPurgeForm(prev => ({ ...prev, nameConfirm: e.target.value }))}
                    placeholder="Type tenant name exactly..."
                    className={tenantPurgeForm.nameConfirm === tenantPurgeModal.preview.tenant_name ? 'border-green-500' : ''}
                    data-testid="purge-name-confirm-input"
                  />
                </div>
                
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="irreversible-confirm"
                    checked={tenantPurgeForm.irreversibleConfirm}
                    onCheckedChange={(checked) => setTenantPurgeForm(prev => ({ ...prev, irreversibleConfirm: checked }))}
                    data-testid="purge-irreversible-checkbox"
                  />
                  <label htmlFor="irreversible-confirm" className="text-sm text-gray-600 cursor-pointer">
                    I understand this action is <strong>irreversible</strong> and all data will be permanently deleted.
                  </label>
                </div>
              </div>
              
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={closeTenantPurgeModal}>Cancel</Button>
                <Button 
                  variant="destructive"
                  onClick={executeTenantPurge}
                  disabled={
                    tenantPurgeLoading ||
                    tenantPurgeForm.nameConfirm !== tenantPurgeModal.preview.tenant_name ||
                    !tenantPurgeForm.irreversibleConfirm
                  }
                  className="bg-red-700 hover:bg-red-800"
                  data-testid="purge-confirm-btn"
                >
                  {tenantPurgeLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Purging...
                    </>
                  ) : (
                    <>
                      <AlertOctagon className="h-4 w-4 mr-2" />
                      Permanently Delete
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
          
          {tenantPurgeModal.step === 'result' && tenantPurgeResult && (
            <div className="space-y-4">
              {tenantPurgeResult.success ? (
                <>
                  <Alert className="border-green-500 bg-green-50">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      <strong>Tenant purged successfully!</strong>
                      <p className="mt-1">{tenantPurgeResult.message}</p>
                    </AlertDescription>
                  </Alert>
                  
                  {tenantPurgeResult.deleted_counts && (
                    <div className="border rounded-lg p-3">
                      <h4 className="font-medium text-sm mb-2">Deleted records:</h4>
                      <div className="grid grid-cols-3 gap-2 text-xs max-h-48 overflow-y-auto">
                        {Object.entries(tenantPurgeResult.deleted_counts).filter(([_, count]) => count > 0).map(([table, count]) => (
                          <div key={table} className="flex justify-between bg-gray-50 rounded px-2 py-1">
                            <span className="text-gray-600">{table}:</span>
                            <span className="font-medium text-green-700">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <Alert className="border-red-500 bg-red-50">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    <strong>Purge failed</strong>
                    <p className="mt-1">{tenantPurgeResult.error}</p>
                  </AlertDescription>
                </Alert>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={closeTenantPurgeModal}>Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Billing Override Modal */}
      <Dialog open={billingOverrideModal.open} onOpenChange={(open) => !open && setBillingOverrideModal({ open: false, tenant: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Billing Override</DialogTitle>
            <DialogDescription>
              {billingOverrideModal.tenant?.name} — set comped billing or revert to Stripe
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Comped (No Billing)</label>
              <Switch
                checked={billingOverrideForm.mode === 'comped'}
                onCheckedChange={(checked) => setBillingOverrideForm(f => ({ ...f, mode: checked ? 'comped' : 'stripe' }))}
                data-testid="comped-toggle"
              />
            </div>
            {billingOverrideForm.mode === 'comped' && (
              <>
                <div>
                  <label className="text-sm font-medium mb-1 block">Plan</label>
                  <Select value={billingOverrideForm.plan} onValueChange={(v) => setBillingOverrideForm(f => ({ ...f, plan: v }))}>
                    <SelectTrigger data-testid="comped-plan-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Starter (1 warehouse, 3 vehicles)</SelectItem>
                      <SelectItem value="growth">Growth (3 warehouses, 10 vehicles)</SelectItem>
                      <SelectItem value="pro">Pro (unlimited)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Reason (optional)</label>
                  <Input
                    placeholder="e.g. friend account, internal testing"
                    value={billingOverrideForm.reason}
                    onChange={(e) => setBillingOverrideForm(f => ({ ...f, reason: e.target.value }))}
                    data-testid="comped-reason-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Expires On (optional)</label>
                  <Input
                    type="date"
                    value={billingOverrideForm.expires_at}
                    onChange={(e) => setBillingOverrideForm(f => ({ ...f, expires_at: e.target.value }))}
                    data-testid="comped-expires-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">Leave blank for never</p>
                </div>
              </>
            )}
            {billingOverrideForm.mode === 'stripe' && billingOverrideModal.tenant?.billing_mode === 'comped' && (
              <Alert>
                <AlertDescription>This will revert to Stripe billing. The tenant will need an active subscription to avoid lockout.</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillingOverrideModal({ open: false, tenant: null })}>Cancel</Button>
            <Button onClick={handleBillingOverride} disabled={billingOverrideLoading} data-testid="billing-override-save">
              {billingOverrideLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Comped Tenant Modal */}
      <Dialog open={compedCreateModal} onOpenChange={(open) => !open && setCompedCreateModal(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Comped Tenant</DialogTitle>
            <DialogDescription>Create a new tenant with no billing — comped access with plan limits</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Tenant Name</label>
              <Input
                placeholder="e.g. Best Friend HVAC"
                value={compedCreateForm.tenant_name}
                onChange={(e) => setCompedCreateForm(f => ({ ...f, tenant_name: e.target.value }))}
                data-testid="comped-tenant-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Owner Name</label>
              <Input
                placeholder="John Smith"
                value={compedCreateForm.owner_name}
                onChange={(e) => setCompedCreateForm(f => ({ ...f, owner_name: e.target.value }))}
                data-testid="comped-owner-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Owner Email</label>
              <Input
                type="email"
                placeholder="john@example.com"
                value={compedCreateForm.owner_email}
                onChange={(e) => setCompedCreateForm(f => ({ ...f, owner_email: e.target.value }))}
                data-testid="comped-owner-email"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Product Family</label>
              <Select value={compedCreateForm.product_family} onValueChange={(v) => setCompedCreateForm(f => ({ ...f, product_family: v }))}>
                <SelectTrigger data-testid="comped-product-family-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inventory_only">Inventory Only</SelectItem>
                  <SelectItem value="inventory_crm">Inventory + CRM</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Plan</label>
              <Select value={compedCreateForm.comped_plan} onValueChange={(v) => setCompedCreateForm(f => ({ ...f, comped_plan: v }))}>
                <SelectTrigger data-testid="comped-create-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter (1 warehouse, 3 vehicles)</SelectItem>
                  <SelectItem value="growth">Growth (3 warehouses, 10 vehicles)</SelectItem>
                  <SelectItem value="pro">Pro (unlimited)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Reason (optional)</label>
              <Input
                placeholder="e.g. internal comp, friend account"
                value={compedCreateForm.reason}
                onChange={(e) => setCompedCreateForm(f => ({ ...f, reason: e.target.value }))}
                data-testid="comped-create-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompedCreateModal(false)}>Cancel</Button>
            <Button
              onClick={handleCompedCreate}
              disabled={compedCreateLoading || !compedCreateForm.tenant_name || !compedCreateForm.owner_name || !compedCreateForm.owner_email}
              data-testid="comped-create-submit"
            >
              {compedCreateLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drip Timeline Drawer */}
      <Sheet open={dripDrawer.open} onOpenChange={(open) => !open && setDripDrawer({ open: false, tenantId: null, tenantName: null })}>
        <SheetContent side="right" className="w-full sm:w-[480px] sm:max-w-[480px] overflow-y-auto p-0">
          <SheetTitle className="sr-only">Drip Timeline</SheetTitle>
          <div className="sticky top-0 bg-card border-b px-4 py-3 z-10">
            <h3 className="font-bold text-base">{dripDrawer.tenantName}</h3>
            {dripTimeline && (
              <p className="text-xs text-gray-500 mt-0.5">
                Day {dripTimeline.days_since_start} of trial
                {dripTimeline.next_email_day && ` — Next: Day ${dripTimeline.next_email_day}`}
              </p>
            )}
          </div>

          {dripTimelineLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : dripTimeline ? (
            <div className="px-4 py-4 space-y-3">
              {dripTimeline.steps.map((step) => (
                <div
                  key={step.key}
                  className={`border rounded-lg p-3 ${
                    step.is_next ? 'border-blue-300 bg-blue-50' :
                    step.status === 'sent' ? 'border-green-200 bg-green-50/30' :
                    step.status === 'failed' ? 'border-red-200 bg-red-50/30' :
                    'border-gray-200'
                  }`}
                  data-testid={`drip-step-${step.day}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-500">Day {step.day}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          step.status === 'sent' ? 'bg-green-100 text-green-700' :
                          step.status === 'failed' ? 'bg-red-100 text-red-700' :
                          step.status === 'pending' ? 'bg-gray-100 text-gray-600' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {step.status === 'sent' ? 'Sent' :
                           step.status === 'failed' ? 'Failed' :
                           step.status === 'pending' ? 'Pending' : 'Skipped'}
                        </span>
                        {step.is_next && <span className="text-xs text-blue-600 font-bold">NEXT</span>}
                      </div>
                      <p className="text-sm font-medium mt-1">{step.label}</p>
                      {step.subject && <p className="text-xs text-gray-500 truncate mt-0.5">{step.subject}</p>}
                      {step.sent_at && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {new Date(step.sent_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="shrink-0 flex gap-1">
                      {step.email_log_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => loadEmailPreview(step.email_log_id)}
                          data-testid={`preview-btn-${step.day}`}
                          className="text-xs h-7 px-2"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Preview
                        </Button>
                      )}
                      {step.is_next && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadNextEmailPreview(dripDrawer.tenantId)}
                          data-testid="preview-next-btn"
                          className="text-xs h-7 px-2"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Preview Next
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {/* Email Preview Panel */}
          {(dripPreview || dripPreviewLoading) && (
            <div className="border-t bg-card">
              <div className="sticky top-0 bg-gray-50 px-4 py-2 flex items-center justify-between">
                <h4 className="text-sm font-bold">Email Preview</h4>
                <Button variant="ghost" size="sm" onClick={() => setDripPreview(null)} className="h-6 w-6 p-0">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              {dripPreviewLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : dripPreview ? (
                <div className="px-4 py-3">
                  <p className="text-xs text-gray-500 mb-1">Subject:</p>
                  <p className="text-sm font-medium mb-3">{dripPreview.subject}</p>
                  {dripPreview.body_html ? (
                    <div
                      className="border rounded bg-card p-3 text-sm max-h-[400px] overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: dripPreview.body_html }}
                      data-testid="email-preview-html"
                    />
                  ) : (
                    <p className="text-xs text-gray-400 italic">No HTML stored for this email.</p>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </SheetContent>
      </Sheet>

    </div>
  );
};

export default AdminTenants;
